import { Button, Dialog, DialogContent, DialogTitle, Chip, Popover, TextField, makeStyles, useTheme } from "@material-ui/core";
import { Close } from "@material-ui/icons";
import Autocomplete, { createFilterOptions } from "@material-ui/lab/Autocomplete";
import React, { useEffect, useRef, useState, useLayoutEffect } from "react";
import { isArray, isString } from "lodash";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { toast } from 'react-toastify';
import { useCommercialContext } from '../../context/Commercial/CommercialContext';
import { executeCommercialCommand } from '../../services/commercialCommands';
import { projectCommercialTags, commercialCardId, commercialErrorMessage } from '../../utils/commercialContext';

const filterTagNames = createFilterOptions();
const filterTags = (options, state) => {
  const input = state.inputValue.trim();
  if (input.startsWith("#")) {
    return /^#\d+$/.test(input)
      ? options.filter(tag => tag.id === Number(input.slice(1)))
      : [];
  }
  return filterTagNames(options, state);
};

const useStyles = makeStyles((theme) => ({
  autocomplete: {
    marginTop: '-4px',
    '& .MuiAutocomplete-inputRoot': {
      padding: '0px 2px',
      minHeight: '19px',
      flexWrap: 'nowrap', // Garante que não quebre linha visualmente no input real
      overflow: 'hidden',
    },
    '& .MuiAutocomplete-input': {
      minWidth: '40px !important',
      padding: '0px 2px !important',
      fontSize: '0.65rem',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    // Estilos do popover/dropdown - FIX TRANSPARÊNCIA COMPLETO
    '& .MuiAutocomplete-popper': {
      zIndex: '9999 !important',
      '& .MuiPaper-root': {
        backgroundColor: `${theme.mode === 'light' ? '#ffffff' : '#1e1e1e'} !important`,
        backdropFilter: 'none !important',
        webkitBackdropFilter: 'none !important',
        background: `${theme.mode === 'light' ? '#ffffff' : '#1e1e1e'} !important`,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: '8px !important',
        boxShadow: theme.mode === 'light'
          ? '0px 4px 20px rgba(0, 0, 0, 0.15) !important'
          : '0px 4px 20px rgba(0, 0, 0, 0.6) !important',
        marginTop: '4px',
        // Evita scrollbar duplicada: quem deve rolar é a listbox (ul), não o Paper
        overflow: 'hidden',
        position: 'relative',
        opacity: '1 !important',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: theme.mode === 'light' ? '#ffffff' : '#1e1e1e',
          borderRadius: '8px',
          zIndex: -1,
        }
      }
    },
    '& .MuiAutocomplete-listbox': {
      padding: '4px 0',
      maxHeight: '200px',
      overflowY: 'auto',
      '& .MuiAutocomplete-option': {
        minHeight: '32px',
        fontSize: '0.75rem',
        padding: '6px 12px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        '&:hover': {
          backgroundColor: theme.mode === 'light'
            ? 'rgba(0, 0, 0, 0.06)'
            : 'rgba(255, 255, 255, 0.08)',
        },
        '&.Mui-focused': {
          backgroundColor: theme.mode === 'light'
            ? 'rgba(0, 0, 0, 0.08)'
            : 'rgba(255, 255, 255, 0.12)',
        },
      }
    }
  },
  tag: {
    margin: '2px',
    fontWeight: 600,
    borderRadius: 4,
    fontSize: "0.65rem",
    height: '19px',
    minHeight: '19px',
    border: theme.palette.type === 'dark' ? "1px solid rgba(255,255,255,0.1)" : "none",
    '& .MuiChip-label': {
      padding: '0 1px 0 6px',
      lineHeight: '19px',
    },
    '& .MuiChip-deleteIcon': {
      width: '12px',
      height: '12px',
      marginLeft: '1px',
      marginRight: '2px',
      display: 'inline-flex',
      color: 'inherit',
    }
  },
  tagCloseIcon: {
    fontSize: "11px !important",
    marginLeft: "2px !important",
    cursor: "pointer",
    opacity: 1,
    "&:hover": {
      opacity: 1,
      stroke: "currentColor",
      strokeWidth: 1,
    },
  },
  moreTag: {
    cursor: 'default',
    userSelect: 'none',
    minWidth: 22,
    justifyContent: 'center',
    '& .MuiChip-label': {
      padding: '0 6px',
      lineHeight: '19px',
    },
  },
  hiddenTagsPaper: {
    backgroundColor: theme.mode === 'light' ? '#ffffff' : '#1e1e1e',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    padding: '6px 8px',
    boxShadow: theme.mode === 'light'
      ? '0px 4px 20px rgba(0, 0, 0, 0.15)'
      : '0px 4px 20px rgba(0, 0, 0, 0.6)',
    maxWidth: 280,
  },
  hiddenTagsList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '4px',
  },
  dropdown: {
    maxWidth: '90vw',
    [theme.breakpoints.up('sm')]: {
      minWidth: 300,
      maxWidth: 400,
    }
  },
  paperComponent: {
    backgroundColor: `${theme.mode === 'light' ? '#ffffff' : '#1e1e1e'} !important`,
    backdropFilter: 'none !important',
    webkitBackdropFilter: 'none !important',
    background: `${theme.mode === 'light' ? '#ffffff' : '#1e1e1e'} !important`,
    border: `1px solid ${theme.palette.divider} !important`,
    borderRadius: '8px !important',
    boxShadow: theme.mode === 'light'
      ? '0px 4px 20px rgba(0, 0, 0, 0.15) !important'
      : '0px 4px 20px rgba(0, 0, 0, 0.6) !important',
    marginTop: '4px !important',
    // Evita scrollbar duplicada: quem deve rolar é a listbox (ul)
    overflow: 'hidden !important',
    position: 'relative !important',
    opacity: '1 !important',
    zIndex: '9999 !important',
    '& .MuiAutocomplete-listbox': {
      ...theme.neoUI.scrollbar.default,
    },
  },
  // Container invisível para medição
  measureContainer: {
    position: 'absolute',
    top: -9999,
    left: 0,
    visibility: 'hidden',
    display: 'flex',
    flexWrap: 'wrap',
    width: '100%',
    padding: '0px 2px', // Mesmo padding do inputRoot
    boxSizing: 'border-box',
    '& .MuiChip-root': {
      // Mesmos estilos da tag
      margin: '2px',
      fontSize: "0.65rem",
      height: '19px',
      minHeight: '19px',
      fontWeight: 600,
      borderRadius: 4,
      border: theme.palette.type === 'dark' ? "1px solid rgba(255,255,255,0.1)" : "none",
      boxSizing: 'border-box',
      '& .MuiChip-label': {
        padding: '0 1px 0 6px',
        lineHeight: '19px',
      },
    }
  }
}));

const TagsContainer = React.memo(({ ticket, onTicketUpdate }) => {
  const commercial = useCommercialContext();
  const [stageChoice, setStageChoice] = useState(null);
  const classes = useStyles();
  const theme = useTheme();
  const [tags, setTags] = useState([]);
  const [selecteds, setSelecteds] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const inputContext = useRef(null);
  const contextEpoch = useRef(0);
  const inputContextKey = `${ticket.id}:${commercial?.enabled ? commercialCardId(commercial.selectedCard) || "none" : "legacy"}`;
  const isMounted = useRef(true);
  const [isFocused, setIsFocused] = useState(false);
  const [hiddenTagsAnchorEl, setHiddenTagsAnchorEl] = useState(null);

  // Estado para controlar quantos tags mostrar
  const [limitTags, setLimitTags] = useState(3);
  const containerRef = useRef(null);
  const measureRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (isFocused && hiddenTagsAnchorEl) {
      setHiddenTagsAnchorEl(null);
    }
  }, [isFocused, hiddenTagsAnchorEl]);

  useLayoutEffect(() => {
    if (inputContext.current === inputContextKey) return;
    inputContext.current = inputContextKey;
    contextEpoch.current += 1;
    setInputValue("");
    setStageChoice(null);
    setSelecteds(Array.isArray(ticket.tags)
      ? commercial?.enabled ? projectCommercialTags(ticket.tags, commercial.selectedCard, tags) : ticket.tags
      : []);
  }, [inputContextKey, ticket.tags, commercial?.enabled, commercial?.selectedCard, tags]);

  useEffect(() => {
    let active = true;
    if (isMounted.current) {
      loadTags(() => active && isMounted.current).then(loadedTags => {
        if (!active || !isMounted.current) return;
        if (Array.isArray(ticket.tags)) {
          setSelecteds(commercial?.enabled ? projectCommercialTags(ticket.tags, commercial.selectedCard, loadedTags || tags) : ticket.tags);
        } else {
          setSelecteds([]);
        }
      });
    }
    return () => { active = false; };
  }, [ticket, commercial?.enabled, commercial?.selectedCard, tags.length]);

  // Observer para redimensionamento
  useLayoutEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });

    resizeObserver.observe(containerRef.current);
    return () => resizeObserver.disconnect();
  }, []);

  // Lógica de cálculo de tags visíveis
  useLayoutEffect(() => {
    if (!measureRef.current || selecteds.length === 0 || containerWidth === 0) {
      setLimitTags(selecteds.length > 0 ? selecteds.length : 1);
      return;
    }

    const container = measureRef.current;
    const children = container.children;

    if (children.length === 0) return;

    let visibleCount = 0;
    const firstTop = children[0].offsetTop;

    for (let i = 0; i < children.length; i++) {
      // Se o elemento mudou de linha (offsetTop maior que o do primeiro)
      if (children[i].offsetTop > firstTop) {
        break;
      }
      visibleCount++;
    }

    // Se nem todos cabem, precisamos reservar espaço para o indicador "+X"
    // Reduzimos 1 para garantir espaço (empiricamente pode precisar ajustar)
    if (visibleCount < selecteds.length) {
      setLimitTags(Math.max(1, visibleCount - 1));
    } else {
      setLimitTags(selecteds.length);
    }

  }, [selecteds, containerWidth]);

  const createTag = async (data) => {
    try {
      const { data: responseData } = await api.post(`/tags`, data);
      return responseData;
    } catch (err) {
      toastError(err);
    }
  };

  const loadTags = async (isCurrent = () => isMounted.current) => {
    try {
      const { data } = await api.get(`/tags/list`);
      if (isCurrent()) setTags(data);
      return data;
    } catch (err) {
      if (isCurrent()) toastError(err);
    }
  };

  const syncTags = async (data) => {
    try {
      const { data: responseData } = await api.post(`/tags/sync`, data);
      return responseData;
    } catch (err) {
      toastError(err);
    }
  };

  const onChange = async (value, reason, explicitTarget = null) => {
    const actionEpoch = contextEpoch.current;
    const isCurrentContext = () => isMounted.current && contextEpoch.current === actionEpoch;
    let optionsChanged = [];
    if (reason === "create-option") {
      if (isArray(value) && value.some(item => isString(item) && item.trim().startsWith("#"))) {
        return;
      }
      if (isArray(value)) {
        for (let item of value) {
          if (isString(item)) {
            const newTag = await createTag({ name: item, color: "#14A45C" });
            optionsChanged.push(newTag);
          } else {
            optionsChanged.push(item);
          }
        }
      }
      if (isCurrentContext()) await loadTags(isCurrentContext);
    } else {
      optionsChanged = value;
    }
    if (commercial?.enabled) {
      if (commercial.readonly) return;
      const target = explicitTarget || (commercial.selectedCard ? { ...commercial.selectedCard } : null);
      const previous = new Map(selecteds.filter(tag => tag.kanbanBoardId).map(tag => [Number(tag.kanbanBoardId), tag]));
      const next = new Map(optionsChanged.filter(tag => tag.kanbanBoardId).map(tag => [Number(tag.kanbanBoardId), tag]));
      try {
        const commands = [];
        for (const boardId of new Set([...previous.keys(), ...next.keys()])) {
          const before = previous.get(boardId);
          const after = next.get(boardId);
          if (before?.id === after?.id) continue;
          const candidates = commercial.cards.filter(card => Number(card.kanbanBoardId) === boardId && !card.archivedAt && (!card.deal || card.deal.state === 'open'));
          const card = Number(target?.kanbanBoardId) === boardId ? target : (candidates.length === 1 ? candidates[0] : null);
          if (!card && candidates.length > 1) {
            if (isCurrentContext()) setStageChoice({ cards: candidates, optionsChanged });
            return;
          }
          if (after) commands.push(['move_card_stage', card, { targetTagId: after.id }, { context: { ticketId: ticket.id, boardId, ...(card ? { cardId: commercialCardId(card), ...(card.dealId ? { dealId: card.dealId } : {}) } : {}) } }]);
          else if (card) commands.push(['archive_card', card, {}, {}]);
        }
        for (const [type, card, payload, options] of commands) await executeCommercialCommand(type, card, ticket.id, payload, options);
        const general = optionsChanged.filter(tag => !tag.kanbanBoardId);
        const projected = (ticket.tags || []).filter(tag => tag.kanbanBoardId);
        const updatedTicket = await syncTags({ ticketId: ticket.id, tags: [...projected, ...general], classificationOnly: true });
        if (!isCurrentContext()) return;
        if (updatedTicket && typeof onTicketUpdate === 'function') onTicketUpdate(updatedTicket);
        await commercial.refresh();
      } catch (error) {
        if (!isCurrentContext()) return;
        toast.error(commercialErrorMessage(error));
        await commercial.refresh();
      }
      return;
    }
    if (isCurrentContext()) setSelecteds(optionsChanged);
    const updatedTicket = await syncTags({ ticketId: ticket.id, tags: optionsChanged });
    if (!isCurrentContext()) return;
    if (updatedTicket?.tags) {
      setSelecteds(updatedTicket.tags);
    }
    if (updatedTicket && typeof onTicketUpdate === "function") {
      onTicketUpdate(updatedTicket);
    }
  };

  const isDarkTheme = theme.palette.type === "dark" || theme.mode === "dark";

  const getTagStyle = (color) => {
    if (color) {
      return {
        backgroundColor: `${color}15`,
        color,
      };
    }

    return {
      backgroundColor: isDarkTheme ? "rgba(255, 255, 255, 0.12)" : "#f5f5f5",
      color: isDarkTheme ? "#e0e0e0" : "#616161",
    };
  };

  const handleMoreTagEnter = (event) => {
    setHiddenTagsAnchorEl(event.currentTarget);
  };

  const handleMoreTagLeave = () => {
    setHiddenTagsAnchorEl(null);
  };

  const visibleLimit = isFocused ? selecteds.length : Math.min(limitTags, selecteds.length);
  const hiddenTags = selecteds.slice(visibleLimit);
  const hiddenTagsIndicatorColor = theme?.palette?.grey?.[500] || "#9e9e9e";
  const hiddenTagsOpen = Boolean(hiddenTagsAnchorEl) && hiddenTags.length > 0 && !isFocused;

  return (
    <div ref={containerRef} style={{ width: '100%', position: 'relative' }}>
      {/* Container Oculto para Medição */}
      <div ref={measureRef} className={classes.measureContainer} style={{ width: containerWidth }}>
        {selecteds.map((option, index) => (
          <Chip
            key={index}
            className={classes.tag}
            label={option.name}
            size="small"
            onDelete={() => { }}
          />
        ))}
        {/* Elemento extra simulando o "+X" para garantir espaço reserva se precisar */}
        <Chip
          className={classes.tag}
          label="+1"
          size="small"
          style={{ visibility: 'hidden' }}
        />
      </div>

      <Autocomplete
        multiple
        size="small"
        options={tags}
        filterOptions={filterTags}
        value={selecteds}
        inputValue={inputValue}
        onInputChange={(_event, value, reason) => {
          // Server projections must not erase a newer search in the same context.
          if (reason === "input" || reason === "clear") setInputValue(value);
        }}
        freeSolo
        disableClearable
        onChange={(e, v, r) => { setInputValue(""); onChange(v, r); }}
        getOptionLabel={(option) => option.name}
        className={classes.autocomplete}
        renderTags={(value, getTagProps) =>
          [
            ...value.slice(0, visibleLimit).map((option, index) => {
              const { className: tagClassName, ...tagProps } = getTagProps({ index });
              const mergedClassName = [tagClassName, classes.tag].filter(Boolean).join(" ");

              return (
                <Chip
                  key={tagProps.key || index}
                  className={mergedClassName}
                  style={getTagStyle(option.color)}
                  label={option.name}
                  size="small"
                  deleteIcon={<Close className={classes.tagCloseIcon} />}
                  {...tagProps}
                />
              );
            }),
            !isFocused && hiddenTags.length > 0 ? (
              <Chip
                key="more-tags"
                className={`${classes.tag} ${classes.moreTag}`}
                label={`+${hiddenTags.length}`}
                size="small"
                style={getTagStyle(hiddenTagsIndicatorColor)}
                onMouseEnter={handleMoreTagEnter}
                onMouseLeave={handleMoreTagLeave}
              />
            ) : null,
          ].filter(Boolean)
        }
        renderOption={(option) => (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            width: '100%'
          }}>
            <div
              style={{
                width: '12px',
                height: '12px',
                backgroundColor: option.color || '#e0e0e0',
                borderRadius: '50%',
                border: '1px solid rgba(0,0,0,0.15)',
                flexShrink: 0,
              }}
            />
            <span style={{
              flex: 1,
              fontSize: '0.75rem',
              color: theme.mode === 'light' ? '#000000' : '#ffffff'
            }}>
              {option.name}
            </span>
          </div>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            placeholder={selecteds.length > 0 ? "" : "+"} // Só mostra + se vazio
            size="small"
            inputProps={{
              ...params.inputProps,
              'aria-label': 'Etiquetas do atendimento e etapa do negócio',
              onFocus: (event) => {
                setIsFocused(true);
                if (params.inputProps.onFocus) {
                  params.inputProps.onFocus(event);
                }
              },
              onBlur: (event) => {
                setIsFocused(false);
                if (params.inputProps.onBlur) {
                  params.inputProps.onBlur(event);
                }
              },
            }}
          />
        )}
        PaperComponent={(props) => (
          <div
            {...props}
            className={`${classes.dropdown} ${classes.paperComponent} ${props.className || ""}`.trim()}
          />
        )}
      />
      <Popover
        open={hiddenTagsOpen}
        anchorEl={hiddenTagsAnchorEl}
        onClose={() => setHiddenTagsAnchorEl(null)}
        disableRestoreFocus
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ className: classes.hiddenTagsPaper }}
        style={{ pointerEvents: 'none' }}
      >
        <div className={classes.hiddenTagsList}>
          {hiddenTags.map((option, index) => (
            <Chip
              key={`${option?.id || option?.name || index}-hidden`}
              className={classes.tag}
              style={getTagStyle(option.color)}
              label={option.name}
              size="small"
            />
          ))}
        </div>
      </Popover>
      <Dialog open={Boolean(stageChoice)} onClose={() => setStageChoice(null)} fullWidth maxWidth="xs">
        <DialogTitle>Selecionar card para esta etapa</DialogTitle><DialogContent>
          {stageChoice?.cards.map(card => <Button fullWidth key={card.id} onClick={() => { const value = stageChoice.optionsChanged; setStageChoice(null); commercial.selectCard(commercialCardId(card)); onChange(value, 'select-option', card); }}>{card.title} · #{card.dealId || card.id}</Button>)}
        </DialogContent>
      </Dialog>
    </div>
  );
});

export { TagsContainer };
